/** @forge-header
 * @id          kalshiApiMarkets
 * @role        Market discovery endpoints | series, events, paginated market fetch
 * @pattern     adapter
 * @purity      api
 * @units       n/a
 * @upstream    kalshiApi/client:authenticatedFetch | types:KalshiSeries,KalshiMarket,KalshiEvent
 * @downstream  kalshiApi/index (via createKalshiClient)
 * @exports     getSeries:fn | getEvents:fn | getMarkets:fn | getMarket:fn | getEvent:fn
 * @test        untested (integration-level)
 */

import type { KalshiSeries, KalshiMarket, KalshiEvent } from '../../types';
import { authenticatedFetch } from './client';

const MONTH_MAP: Record<string, number> = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12,
};

/**
 * Extract game date (YYYY-MM-DD) from an event ticker like KXNBAGAME-26MAR04OKCNYK.
 * The date portion is YYMONDD starting after the first hyphen.
 */
function parseGameDateFromEventTicker(ticker: string): string | null {
  const parts = ticker.split('-');
  if (parts.length < 2) return null;
  const mid = parts[1] ?? '';
  if (mid.length < 7) return null;

  const yy = Number(mid.slice(0, 2));
  const monStr = mid.slice(2, 5).toUpperCase();
  const dd = Number(mid.slice(5, 7));
  const mm = MONTH_MAP[monStr];

  if (!Number.isFinite(yy) || !Number.isFinite(dd) || !mm) return null;

  const year = 2000 + yy;
  return `${year}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
}

/**
 * Get series (categories)
 */
export async function getSeries(
  params: { category?: string; include_volume?: boolean } = {}
): Promise<KalshiSeries[]> {
  const query: Record<string, string> = {};
  if (params.category) {
    query.category = params.category;
  }
  // Always include volume for richer display
  query.include_volume = params.include_volume !== false ? 'true' : 'false';

  const response = await authenticatedFetch('GET', '/trade-api/v2/series', query);
  const data = await response.json();

  // Handle different response formats and normalize ticker field
  const normalize = (series: KalshiSeries[]): KalshiSeries[] =>
    series.map((s) => ({
      ...s,
      series_ticker: s.series_ticker || s.ticker || '',
    }));

  if (Array.isArray(data)) {
    return normalize(data);
  }
  if (data.series && Array.isArray(data.series)) {
    return normalize(data.series);
  }
  if (data.results && Array.isArray(data.results)) {
    return normalize(data.results);
  }

  return [];
}

/**
 * Get events (for a series or filtered by status)
 */
export async function getEvents(
  params: {
    series_ticker?: string;
    status?: 'open' | 'closed' | 'settled';
    with_nested_markets?: boolean;
    limit?: number;
  } = {}
): Promise<KalshiEvent[]> {
  const query: Record<string, string> = {};
  if (params.series_ticker) query.series_ticker = params.series_ticker;
  if (params.status) query.status = params.status;
  if (params.with_nested_markets) query.with_nested_markets = 'true';
  if (params.limit) query.limit = params.limit.toString();

  const response = await authenticatedFetch('GET', '/trade-api/v2/events', query);
  const data = await response.json();

  // Handle different response formats
  if (Array.isArray(data)) {
    return data;
  }
  if (data.events && Array.isArray(data.events)) {
    return data.events;
  }
  if (data.results && Array.isArray(data.results)) {
    return data.results;
  }

  return [];
}

/**
 * Get markets
 */
export async function getMarkets(
  params: {
    series_ticker?: string;
    event_ticker?: string;
    status?: string;
    limit?: number;
  } = {}
): Promise<KalshiMarket[]> {
  // Normalize ticker field - API returns 'ticker' but we use 'market_ticker'
  // Also map `expiration_time` → `expiry_time` (API returns `expiration_time`)
  const normalize = (markets: KalshiMarket[]): KalshiMarket[] =>
    markets.map((m) => ({
      ...m,
      market_ticker: m.market_ticker || (m as unknown as { ticker?: string }).ticker || '',
      expiry_time:
        m.expiry_time || (m as unknown as { expiration_time?: string }).expiration_time || '',
    }));

  const pageLimit = params.limit ?? 200;
  const maxPages = 5; // Cap to avoid runaway pagination
  const pageDelayMs = 500; // Delay between pages to respect rate limits

  const allMarkets: KalshiMarket[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < maxPages; page++) {
    const query: Record<string, string> = {};
    if (params.series_ticker) query.series_ticker = params.series_ticker;
    if (params.event_ticker) query.event_ticker = params.event_ticker;
    if (params.status) query.status = params.status;
    query.limit = pageLimit.toString();
    if (cursor) query.cursor = cursor;

    const response = await authenticatedFetch('GET', '/trade-api/v2/markets', query);
    const data = await response.json();

    // Extract markets from response
    let pageMarkets: KalshiMarket[] = [];
    if (Array.isArray(data)) {
      pageMarkets = data;
    } else if (data.markets && Array.isArray(data.markets)) {
      pageMarkets = data.markets;
    } else if (data.results && Array.isArray(data.results)) {
      pageMarkets = data.results;
    }

    allMarkets.push(...pageMarkets);

    // Check for next page cursor
    const nextCursor = data.cursor as string | undefined;
    if (!nextCursor || pageMarkets.length < pageLimit) {
      break; // No more pages
    }

    cursor = nextCursor;

    // Delay between pages to avoid rate limiting
    if (page < maxPages - 1) {
      await new Promise((resolve) => setTimeout(resolve, pageDelayMs));
    }
  }

  return normalize(allMarkets);
}

/**
 * Get a single market by ticker (to access the market title).
 */
export async function getMarket(marketTicker: string): Promise<KalshiMarket | null> {
  try {
    const response = await authenticatedFetch(
      'GET',
      `/trade-api/v2/markets/${encodeURIComponent(marketTicker)}`
    );
    const data = await response.json();
    // Kalshi returns { market: { ... } } for this endpoint per docs.
    const rec = data as unknown as Record<string, unknown>;
    const market =
      (rec && typeof rec === 'object' && 'market' in rec
        ? (rec as { market?: unknown }).market
        : null) ?? data;
    return market as KalshiMarket;
  } catch (error) {
    console.warn(`Failed to fetch market ${marketTicker}:`, error);
    return null;
  }
}

/**
 * Get event details
 */
export async function getEvent(eventTicker: string): Promise<KalshiEvent | null> {
  try {
    const response = await authenticatedFetch('GET', `/trade-api/v2/events/${eventTicker}`);
    const data = await response.json();
    // API returns { event: {...}, markets: [...] } wrapper
    if (data && typeof data === 'object' && 'event' in data) {
      const event = data.event as KalshiEvent;

      // [TIME-DIAG] Log raw event data for time chain tracing
      console.log(
        `[TIME-DIAG] getEvent(${eventTicker}) — event.start_time=${event.start_time ?? 'MISSING'}, markets=${data.markets?.length ?? 0}`
      );

      // If event doesn't have start_time but markets do have close_time, use that
      // Prefer moneyline markets (close at game start) over spread/total (close +1h)
      if (
        !event.start_time &&
        data.markets &&
        Array.isArray(data.markets) &&
        data.markets.length > 0
      ) {
        const markets = data.markets as Array<{
          ticker?: string;
          close_time?: string;
          subtitle?: string;
        }>;

        // Try moneyline market first (ticker contains no T/S suffix, or subtitle is empty/moneyline)
        const moneylineMarket = markets.find((m) => {
          const t = m.ticker ?? '';
          // Moneyline tickers end with team name, not -T## or -S##
          return m.close_time && !/-[TS]\d/i.test(t);
        });

        let rawCloseTime: string | undefined;
        if (moneylineMarket?.close_time) {
          rawCloseTime = moneylineMarket.close_time;
          console.log(
            `[TIME-DIAG] getEvent(${eventTicker}) — used moneyline market close_time: ${moneylineMarket.ticker} → ${rawCloseTime}`
          );
        } else {
          // Fall back to earliest close_time across all markets
          const closeTimes = markets
            .map((m) => m.close_time)
            .filter((ct): ct is string => !!ct)
            .sort();
          if (closeTimes.length > 0) {
            rawCloseTime = closeTimes[0];
            console.warn(
              `[TIME-DIAG] getEvent(${eventTicker}) — WARNING: no moneyline market, used earliest close_time: ${rawCloseTime} (of ${closeTimes.length} markets)`
            );
          }
        }

        if (rawCloseTime) {
          // DST fix: close_time dates can be 15+ days after the actual game,
          // crossing DST boundaries. Extract time-of-day from close_time and
          // transplant it onto the real game date (from ticker) so Intl.DateTimeFormat
          // applies the correct UTC offset for the game's actual date.
          const gameDate = parseGameDateFromEventTicker(eventTicker);
          if (gameDate) {
            const timeOfDay = rawCloseTime.split('T')[1] ?? '00:00:00Z';
            const corrected = `${gameDate}T${timeOfDay}`;
            event.start_time = corrected;
            console.log(
              `[TIME-DIAG] getEvent(${eventTicker}) — DST transplant: close=${rawCloseTime} → game date ${gameDate} → ${corrected}`
            );
          } else {
            // Can't parse game date from ticker — use close_time as-is
            event.start_time = rawCloseTime;
          }
        }
      }
      return event;
    }
    return data as KalshiEvent;
  } catch (error) {
    console.warn(`Failed to fetch event ${eventTicker}:`, error);
    return null;
  }
}
